/**
 * Delivery-copy helpers — derive customer-facing pickup/delivery wording from
 * the tenant's delivery-fee configuration, so storefront copy stays in sync
 * with what the tenant actually bills (no hardcoded "free" claims).
 *
 * The tenant config is surfaced by the backend (validation.py / shop-info) as:
 *   deliveryFeeMode:          'none' | 'flat' | 'distance' | 'tiered'
 *   deliveryFeeFreeRadiusMi:  number   (distance mode: first N miles free)
 *   deliveryFeeTiers:         [{ upToMi|up_to_mi, flat, ... }, ...]  (tiered)
 */

/**
 * Return the number of "free pickup/delivery" miles the tenant offers, or null
 * when there is no free-distance allowance to advertise.
 *
 *   - distance mode: deliveryFeeFreeRadiusMi (if > 0)
 *   - tiered mode:   the largest leading tier whose fee is 0 (the free band)
 *   - none/flat/unset: null (no distance-based free allowance to promise)
 *
 * @param {object} config  tenant config (accepts camelCase or the raw fields)
 * @returns {number|null}
 */
export function freePickupMiles(config) {
  if (!config) return null;
  const mode = config.deliveryFeeMode || config.delivery_fee_mode || 'none';

  if (mode === 'distance') {
    const r = Number(
      config.deliveryFeeFreeRadiusMi ?? config.delivery_fee_free_radius_mi ?? 0
    );
    return Number.isFinite(r) && r > 0 ? r : null;
  }

  if (mode === 'tiered') {
    const tiers = config.deliveryFeeTiers || config.delivery_fee_tiers || [];
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    // The free band is the leading tier(s) with a 0 fee. Take the furthest
    // "up to" mileage among the zero-fee leading tiers.
    let freeUpTo = 0;
    // Sort by upTo ascending so we can walk the leading free brackets.
    const sorted = [...tiers].sort((a, b) => {
      const au = Number(a.upToMi ?? a.up_to_mi ?? Infinity);
      const bu = Number(b.upToMi ?? b.up_to_mi ?? Infinity);
      return au - bu;
    });
    for (const t of sorted) {
      const fee = Number(t.flat ?? t.fee ?? 0);
      const upTo = Number(t.upToMi ?? t.up_to_mi ?? 0);
      if (fee === 0 && Number.isFinite(upTo) && upTo > 0) {
        freeUpTo = Math.max(freeUpTo, upTo);
      } else {
        // First non-free bracket ends the free band.
        break;
      }
    }
    return freeUpTo > 0 ? freeUpTo : null;
  }

  return null;
}

/**
 * A tenant-safe pickup/delivery blurb. When the tenant has a free-mileage
 * allowance, we can honestly say "Pickup included within the first N miles.";
 * otherwise we use a neutral "Pickup & delivery available." — never a blanket
 * "free" promise.
 *
 * @param {object} config
 * @returns {string}
 */
export function pickupInclusionLine(config) {
  const miles = freePickupMiles(config);
  if (miles) {
    const n = miles % 1 === 0 ? miles : miles.toFixed(1);
    return `Pickup included within the first ${n} miles.`;
  }
  return 'Pickup & delivery available.';
}
